'use client';

import { memo, useCallback, useMemo, useState } from 'react';
import { usePathname } from 'next/navigation';
import {
  CreditsUsedPopup,
  creditsUsedSnapshot,
  type CreditsUsedSnapshot,
} from '@/new_components/shared/CreditsUsedPopup';
import { InsufficientCreditsDialog } from '@/new_components/shared/InsufficientCreditsDialog';
import { SignupRequiredDialog } from '@/new_components/shared/SignupRequiredDialog';
import { Download, ExternalLink, FileText } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { WEB_CREDIT_COSTS } from '@/config/credit-costs.config';
import { trackFileDownload } from '@/lib/analytics';
import { deliverFirmenbuchResponse } from '@/lib/bilanz-files';
import { openPendingTab } from '@/lib/document-tab';
import type { FinancialFileItem } from '@/lib/backend-api';

/** Which document a log row is about, for /admin/logs. */
function fileLogMeta(file: FinancialFileItem) {
  return {
    fileId: file.file_id ?? null,
    publicationId: file.id,
    fileType: file.file_type,
    year: file.year ?? null,
    name: file.public_name?.slice(0, 300) ?? null,
    bilanzType: file.bilanz_type ?? null,
    publishedAt: file.published_at?.slice(0, 10) ?? null,
  };
}
import { loggedToast } from '@/lib/logged-toast';
import { useApplyCreditBalance } from '@/hooks/use-credit-balance';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { SourceAttribution } from './SourceAttribution';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { LocaleLink } from '@/components/locale-link';
import {
  DOWNLOADABLE_FILE_TYPES,
  formatCapturedDate,
} from './companyDetailUtils';

// Local-time timestamp + filename slug for the exported PDF.
function fileTimestamp(): string {
  const d = new Date();
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}_${pad(d.getHours())}-${pad(d.getMinutes())}-${pad(d.getSeconds())}`;
}
function slugify(name: string): string {
  return (
    name
      .normalize('NFD')
      .replace(/[̀-ͯ]/g, '')
      .replace(/[^a-zA-Z0-9]+/g, '_')
      .replace(/^_+|_+$/g, '')
      .slice(0, 60) || 'bilanz'
  );
}

/**
 * Right-column card listing downloadable Jahres-/Konzernabschluss files (Bilanz etc.).
 *
 * Only renders files whose `file_type` is in {@link DOWNLOADABLE_FILE_TYPES}; hides itself
 * entirely when that list is empty. "Bilanz" files are opened in an in-page dialog that
 * streams HTML content from the backend; other downloads open the presigned URL in a new tab.
 * Guests see a sign-in prompt instead of the download button.
 */
export const FinancialDocsCard = memo(function FinancialDocsCard({
  financialFiles,
  euId,
  isAuthenticated,
  canDownload = false,
  className,
  id,
}: {
  financialFiles: FinancialFileItem[];
  euId: string;
  isAuthenticated: boolean;
  /** Whether the user may download the Bilanz HTML (Pro/Admin). The button is
   *  shown to everyone viewing the popup; non-entitled users get a Pro prompt. */
  canDownload?: boolean;
  /** Layout classes from the page (mobile `order-*`, borders) — see MOBILE_ORDER. */
  className?: string;
  /** Scroll anchor for the mobile action bar. */
  id?: string;
}) {
  const { t, i18n } = useTranslation();
  const pathname = usePathname();
  const [downloadingFileId, setDownloadingFileId] = useState<number | null>(
    null,
  );
  const [bilanzModalOpen, setBilanzModalOpen] = useState(false);
  const [bilanzContent, setBilanzContent] = useState<string | null>(null);
  const [bilanzTitle, setBilanzTitle] = useState<string>('');
  // Underlying file id of the open Bilanz — the HTML export charges against
  // it (`bilanz_html_download:<id>` entitlement), so re-exporting the same
  // document is free.
  const [bilanzFileId, setBilanzFileId] = useState<number | null>(null);
  const applyCreditBalance = useApplyCreditBalance();
  const [proPromptOpen, setProPromptOpen] = useState(false);
  // Guests clicking "Sign in" on a document get the account-required modal first.
  const [signupDialogOpen, setSignupDialogOpen] = useState(false);
  // Centered "out of credits" modal with a link to the pricing page —
  // replaces the corner toast for 402 responses.
  const [creditsDialogOpen, setCreditsDialogOpen] = useState(false);
  // Centered "N credits used, M left" confirmation after a successful charge.
  const [creditsUsed, setCreditsUsed] = useState<CreditsUsedSnapshot | null>(
    null,
  );

  // Download the currently-open Bilanz as a standalone .html file. The content
  // is the raw register HTML already fetched for the viewer, so this is a
  // direct one-click download (Blob + anchor) — no conversion, no extra deps.
  // Pro/Admin only.
  const handleDownloadHtml = useCallback(async () => {
    if (!canDownload) {
      setProPromptOpen(true);
      return;
    }
    if (!bilanzContent) return;
    // The HTML export costs credits. The price is fixed server-side per
    // action name; a 402 blocks the download, any other charge failure is
    // surfaced but never silently free-rides.
    try {
      const chargeRes = await fetch('/api/credits/charge', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: 'bilanz_html_download',
          // File id feeds the entitlement key — Bilanz publications are
          // immutable, so once paid the export is free from then on.
          ref: bilanzFileId != null ? String(bilanzFileId) : euId,
          // Only labels the resulting Downloads notification; billing
          // ignores it.
          euId,
        }),
      });
      if (!chargeRes.ok) {
        if (chargeRes.status === 402) {
          setCreditsDialogOpen(true);
        } else {
          loggedToast.error(
            t('companyDetail.registerDocs.errors.generic', {
              name: 'Bilanz HTML',
            }),
          );
        }
        return;
      }
      const snapshot = (await chargeRes.json().catch(() => null)) as {
        charged?: number;
        balance?: number;
        entitled?: boolean;
      } | null;
      setCreditsUsed(
        creditsUsedSnapshot(snapshot?.charged, snapshot?.balance, {
          entitled: snapshot?.entitled === true,
          document: true,
        }),
      );
      applyCreditBalance(snapshot?.balance);
    } catch {
      loggedToast.error(
        t('companyDetail.registerDocs.errors.generic', { name: 'Bilanz HTML' }),
      );
      return;
    }
    // If the stored content isn't already a full document, wrap it so the file
    // opens cleanly on its own; otherwise keep it verbatim.
    const looksLikeDoc = /<html[\s>]/i.test(bilanzContent);
    const safeTitle = (bilanzTitle || 'Bilanz').replace(/[<>]/g, '');
    const html = looksLikeDoc
      ? bilanzContent
      : `<!doctype html><html><head><meta charset="utf-8"><title>${safeTitle}</title></head><body>${bilanzContent}</body></html>`;
    const blob = new Blob([html], { type: 'text/html;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${slugify(bilanzTitle || 'bilanz')}_${fileTimestamp()}.html`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
    trackFileDownload(euId, 'Bilanz-HTML', 0);
  }, [
    canDownload,
    bilanzContent,
    bilanzTitle,
    bilanzFileId,
    euId,
    t,
    applyCreditBalance,
  ]);

  const filteredFiles = useMemo(
    () =>
      financialFiles.filter((f) =>
        DOWNLOADABLE_FILE_TYPES.includes(f.file_type),
      ),
    [financialFiles],
  );

  const handleDownload = useCallback(
    async (file: FinancialFileItem) => {
      // `file.id` is the publication row id; downloads go through the
      // underlying `file.file_id`, which is null until the Bilanz HTML
      // has actually been downloaded and stored.
      const downloadFileId = file.file_id;
      if (
        !isAuthenticated ||
        downloadFileId == null ||
        !(file.obj_storage_key || file.has_file) ||
        !DOWNLOADABLE_FILE_TYPES.includes(file.file_type)
      ) {
        fetch('/api/log/page-view', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            event: 'document_download_failed',
            entityId: euId,
            entityType: 'file',
            meta: fileLogMeta(file),
            description: `Cannot download ${file.file_type}: ${!isAuthenticated ? 'not authenticated' : downloadFileId == null ? 'no file available' : !(file.obj_storage_key || file.has_file) ? 'no file available' : 'unsupported type'}`,
            url: window.location.pathname,
          }),
          keepalive: true,
        }).catch(() => {});
        return;
      }
      // An Austrian statement may come back as a PDF to show in a new tab;
      // the tab must open now, before the first await, or the popup blocker
      // wins (lib/document-tab.ts). It is closed again if the answer is an
      // XML to save or an error.
      const pdfTab =
        file.file_type === 'Bilanz' && file.source === 'firmenbuch'
          ? openPendingTab()
          : null;
      let tabHandedOver = false;
      setDownloadingFileId(file.id);
      try {
        const response = await fetch(
          `/api/companies/${encodeURIComponent(euId)}/files/${downloadFileId}/download`,
        );
        // Parse manually so a non-JSON body (proxy/gateway timeout HTML, empty
        // 5xx, etc.) yields a descriptive error instead of Safari's opaque
        // "The string did not match the expected pattern".
        const rawText = await response.text();
        let result: { success?: boolean; data: { url: string } };
        try {
          result = JSON.parse(rawText);
        } catch {
          throw new Error(
            `Non-JSON response (${response.status}): ${rawText.slice(0, 300)}`,
          );
        }
        if (!response.ok || !result.success) {
          fetch('/api/log/page-view', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              event: 'document_download_failed',
              entityId: euId,
              entityType: 'file',
              meta: fileLogMeta(file),
              description: `Failed to download ${file.file_type} (${response.status}) body=${rawText.slice(0, 500)}`,
              url: window.location.pathname,
            }),
            keepalive: true,
          }).catch(() => {});
          return;
        }
        trackFileDownload(euId, file.file_type, downloadFileId);
        // The download route logs document_download server-side with the file
        // name; logging it here too counted every Bilanz twice.
        if (file.file_type === 'Bilanz') {
          const contentResponse = await fetch(
            `/api/companies/${encodeURIComponent(euId)}/files/${downloadFileId}/content`,
          );
          if (contentResponse.ok) {
            // Austrian Firmenbuch statement: its PDF opens in the pending
            // tab, an XML is saved as a file; never the HTML viewer.
            tabHandedOver = true;
            const delivered = await deliverFirmenbuchResponse(
              contentResponse,
              pdfTab,
              file.public_name || 'Jahresabschluss',
            );
            if (!delivered) {
              setBilanzContent(await contentResponse.text());
              setBilanzTitle(file.public_name || 'Bilanz');
              setBilanzFileId(downloadFileId);
              setBilanzModalOpen(true);
            }
            // The view was billed inside the content route; the charge
            // snapshot rides along as headers because the body is HTML.
            const charged = Number(
              contentResponse.headers.get('X-Credits-Charged'),
            );
            const balance = Number(
              contentResponse.headers.get('X-Credits-Balance'),
            );
            setCreditsUsed(
              creditsUsedSnapshot(charged, balance, {
                entitled:
                  contentResponse.headers.get('X-Credits-Entitled') === 'true',
                document: true,
              }),
            );
            if (contentResponse.headers.has('X-Credits-Balance')) {
              applyCreditBalance(balance);
            }
          } else {
            if (contentResponse.status === 402) {
              setCreditsDialogOpen(true);
            }
            fetch('/api/log/page-view', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                event: 'document_download_failed',
                entityId: euId,
                entityType: 'file',
                meta: fileLogMeta(file),
                description: `Failed to load Bilanz content (${contentResponse.status})`,
                url: window.location.pathname,
              }),
              keepalive: true,
            }).catch(() => {});
          }
        } else {
          window.open(result.data.url, '_blank');
        }
      } catch (err) {
        fetch('/api/log/page-view', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            event: 'document_download_failed',
            entityId: euId,
            entityType: 'file',
            meta: fileLogMeta(file),
            description: `Failed to download ${file.file_type}: ${err instanceof Error ? err.message : 'unknown error'}`,
            url: window.location.pathname,
          }),
          keepalive: true,
        }).catch(() => {});
      } finally {
        if (!tabHandedOver) pdfTab?.close();
        setDownloadingFileId(null);
      }
    },
    [euId, isAuthenticated, applyCreditBalance],
  );

  /**
   * Send the user to the Unternehmensregister homepage in their UI
   * language. Used for publications whose Bilanz HTML hasn't been
   * downloaded yet — the precise per-publication deep link isn't
   * stable, so we fall back to the registry's landing page where the
   * user can search themselves.
   */
  const handleGoToSource = useCallback(() => {
    const locale = i18n.language?.toLowerCase().startsWith('de') ? 'de' : 'en';
    window.open(
      `https://www.unternehmensregister.de/${locale}`,
      '_blank',
      'noopener,noreferrer',
    );
  }, [i18n.language]);

  // Austrian companies' statements come from the Firmenbuch (CC BY 4.0,
  // credited page-wide by RegisterSourceNote); German ones from the
  // Unternehmensregister / Bundesanzeiger.
  const filingsSource = filteredFiles.some((f) => f.source === 'firmenbuch')
    ? 'firmenbuch'
    : 'unternehmensregister';

  if (filteredFiles.length === 0) return null;

  return (
    <Card variant="grid" className={className} id={id}>
      <CardHeader>
        <CardTitle as="h2">
          {t(
            'companyDetail.financialPublications',
            'Annual Financial Statements',
          )}
        </CardTitle>
      </CardHeader>
      <CardContent className="px-4 pb-4 pt-0">
        <div className="flex flex-col divide-y max-h-[400px] overflow-y-auto">
          {filteredFiles.map((file) => (
            <div
              key={file.id}
              className="flex items-center gap-2 sm:gap-3 py-1 sm:py-3"
            >
              <FileText className="h-4 w-4 sm:h-5 sm:w-5 text-muted-foreground shrink-0" />
              <div className="flex flex-col gap-0.5 min-w-0 flex-1">
                <span className="text-[10px] sm:text-sm font-medium truncate">
                  {(() => {
                    const name = file.public_name || 'Bilanz';
                    const years = name.match(/\d{4}/g);
                    const lastYear = years?.[years.length - 1];
                    const isKonzern = /konzern/i.test(name);
                    if (lastYear) {
                      return isKonzern
                        ? t('companyDetail.konzernabschluss', {
                            year: lastYear,
                            defaultValue: 'Konzernabschluss {{year}}',
                          })
                        : t('companyDetail.jahresabschluss', {
                            year: lastYear,
                            defaultValue: 'Jahresabschluss {{year}}',
                          });
                    }
                    return name;
                  })()}
                </span>
                {/* Publication date from the Bundesanzeiger row. Rendered as
                    a <time> element in the SSR HTML so crawlers and AI
                    scrapers get a machine-readable date per filing. */}
                {file.published_at && (
                  <span className="text-[9px] sm:text-xs leading-tight text-muted-foreground truncate">
                    {t('companyDetail.publishedAt', 'Published on')}{' '}
                    <time dateTime={file.published_at}>
                      {formatCapturedDate(file.published_at, i18n.language) ??
                        file.published_at}
                    </time>
                  </span>
                )}
              </div>
              <div className="flex items-center gap-1.5 shrink-0">
                {isAuthenticated && file.file_id != null && (
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          variant="outline"
                          size="sm"
                          className="min-h-[44px] sm:min-h-0 -my-2 sm:my-0 h-auto sm:h-7 text-[10px] sm:text-sm lg:text-xs px-2 sm:px-3 border-0 lg:border bg-transparent lg:bg-background rounded-full shadow-none"
                          disabled={
                            !(file.obj_storage_key || file.has_file) ||
                            downloadingFileId === file.id
                          }
                          onClick={() => handleDownload(file)}
                        >
                          <Download className="h-3 w-3 sm:h-3.5 sm:w-3.5 mr-1" />
                          {downloadingFileId === file.id
                            ? '...'
                            : t('companyDetail.registerDocs.view', 'View')}
                        </Button>
                      </TooltipTrigger>
                      {/* Price on hover/focus, before the click. */}
                      <TooltipContent>
                        {t('credits.costTooltip', {
                          n: WEB_CREDIT_COSTS.bilanzView,
                          defaultValue:
                            'Costs {{n}} credits — once you have paid for a document it stays free.',
                        })}
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                )}
                {isAuthenticated && file.file_id == null && (
                  <Button
                    variant="outline"
                    size="sm"
                    className="min-h-[44px] sm:min-h-0 -my-2 sm:my-0 h-auto sm:h-7 text-[10px] sm:text-sm lg:text-xs px-2 sm:px-3 border-0 lg:border bg-transparent lg:bg-background rounded-full shadow-none"
                    onClick={handleGoToSource}
                  >
                    <ExternalLink className="h-3 w-3 sm:h-3.5 sm:w-3.5 mr-1" />
                    {t('companyDetail.registerDocs.goToSource', 'Go to source')}
                  </Button>
                )}
                {!isAuthenticated && (
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          variant="outline"
                          size="sm"
                          className="min-h-[44px] sm:min-h-0 -my-2 sm:my-0 h-auto sm:h-7 text-[10px] sm:text-sm lg:text-xs px-2 sm:px-3 whitespace-nowrap border-0 lg:border bg-transparent lg:bg-background rounded-full shadow-none"
                          onClick={() => setSignupDialogOpen(true)}
                        >
                          <Download className="h-3 w-3 sm:h-3.5 sm:w-3.5 mr-1" />
                          {t('companyDetail.registerDocs.signup', 'Sign in')}
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>
                        {t('companyDetail.registerDocs.loginToDownload')}
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                )}
              </div>
            </div>
          ))}
        </div>
        <SourceAttribution source={filingsSource} />
      </CardContent>

      <Dialog
        open={bilanzModalOpen}
        onOpenChange={(open) => {
          setBilanzModalOpen(open);
          if (!open) {
            setBilanzContent(null);
            setBilanzFileId(null);
          }
        }}
      >
        <DialogContent
          variant="fullscreen"
          className="max-h-[90vh] overflow-hidden flex flex-col"
        >
          <DialogHeader className="flex flex-row items-center justify-between gap-3 pr-10">
            <DialogTitle className="truncate">{bilanzTitle}</DialogTitle>
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="outline"
                    size="sm"
                    className="shrink-0 gap-1.5 rounded-full"
                    onClick={handleDownloadHtml}
                    disabled={!bilanzContent}
                  >
                    <Download className="h-3.5 w-3.5 shrink-0" />
                    {t('companyDetail.downloadHtml', 'HTML herunterladen')}
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  {t('credits.costTooltip', {
                    n: WEB_CREDIT_COSTS.bilanzHtmlDownload,
                    defaultValue:
                      'Costs {{n}} credits — once you have paid for a document it stays free.',
                  })}
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </DialogHeader>
          <DialogBody className="overflow-auto flex-1 min-h-0">
            {bilanzContent && (
              <div
                className="bilanz-table-container rounded-md border border-border bg-card p-2 sm:p-4 text-foreground text-[5px] sm:text-sm [&_table]:max-sm:w-full [&_table]:max-sm:table-fixed [&_table]:text-[5px] [&_table]:sm:text-sm [&_td]:p-0.5 [&_td]:sm:p-1 [&_th]:p-0.5 [&_th]:sm:p-1 [&_td]:break-words [&_td]:whitespace-normal [&_th]:break-words [&_th]:whitespace-normal overflow-x-hidden dark:[&_*]:!bg-transparent dark:[&_tr:nth-child(even)]:!bg-white/5 dark:[&_th]:!bg-white/10 dark:[&_*]:!text-foreground dark:[&_*]:!border-border"
                dangerouslySetInnerHTML={{ __html: bilanzContent }}
              />
            )}
          </DialogBody>
        </DialogContent>
      </Dialog>

      <SignupRequiredDialog
        open={signupDialogOpen}
        onOpenChange={setSignupDialogOpen}
        pathname={pathname}
        source="financial_docs"
        companyId={euId}
      />

      <InsufficientCreditsDialog
        open={creditsDialogOpen}
        onOpenChange={setCreditsDialogOpen}
      />

      {/* Non-blocking, auto-dismissing — renders above the Bilanz viewer
          when both appear from the same click. */}
      <CreditsUsedPopup
        snapshot={creditsUsed}
        onClose={() => setCreditsUsed(null)}
      />

      {/* Pro-feature prompt shown when a non-entitled user clicks Download PDF. */}
      <Dialog open={proPromptOpen} onOpenChange={setProPromptOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>
              {t('search.proFeature.title', 'Pro-Funktion')}
            </DialogTitle>
            <DialogDescription>
              {t(
                'companyDetail.downloadHtmlProPrompt',
                'Der Download der Jahresabschlüsse ist nur für Pro-Nutzer verfügbar. Mit einem Pro-Konto können Sie Bilanzen herunterladen.',
              )}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setProPromptOpen(false)}>
              {t('common.buttons.close', 'Schließen')}
            </Button>
            <Button asChild>
              <LocaleLink href="/plans" onClick={() => setProPromptOpen(false)}>
                {t('search.proFeature.cta', 'Pläne ansehen')}
              </LocaleLink>
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
});
